import type { DateTime } from "luxon";
import { actualMatchesScheduled } from "../../components/StaffTable/utils";
import { type NurseFullRole, NurseFullRoleShort, type NurseInShift } from "../../utils/models";
import { formatShiftTimeRange } from "../dailyPrintout/util/shiftTimeFormat";

/**
 * Who lands on which CDPH 530 page, and what each row says.
 *
 * Rendering-agnostic on purpose: `create530Excel` turns these pages into the workbook that IS the
 * form — previewed, downloaded as xlsx, and rendered to PDF for download and print — so the
 * bucketing, punch pairing and midnight-crossing rules live here rather than in a renderer.
 */

export type Shift530 = { id: string; startTimeLabel: string; startHour: number };

export type Shift530Filter = "all" | "1" | "2" | "3";

/**
 * Per-facility 530 customization (APP-647). Both default to off, which reproduces the original blank
 * form: the columns are always present, these only decide whether they arrive pre-filled.
 */
export type Form530Settings = {
	shiftFilter: Shift530Filter;
	/** Pre-fill "ACTUAL SHIFT START/END" with the assignment's own times. */
	showShiftTimes: boolean;
	/** Pre-fill "NURSING SERVICES ASSIGNMENT" with the assignment's location. */
	showLocations: boolean;
};

export const DEFAULT_FORM_530_SETTINGS: Form530Settings = { shiftFilter: "all", showShiftTimes: false, showLocations: false };

export const SIGNATURE_MARK = "X";
export const MIN_TABLE_ROWS = 7;

export const FORM_REVISION = "CDPH 530 (06/19)";
export const AGENCY_LINE = "State of California-Health and Human Service Agency";
export const DEPARTMENT_LINE = "California Department of Public Health";
export const TITLE = "NURSING STAFFING ASSIGNMENT AND SIGN-IN SHEET";
export const SUBTITLE = "(DHPPD SALARIED/DUAL ROLE/NURSE ASSISTANT)";
export const VERIFICATION_TEXT =
	"I have reviewed and verified all staffing assignments are true and accurate. Employees not captured in payroll records, nurse assistants or employees who are primarily engaged in duties other than nursing services that provided nursing services during the patient day are recorded and their direct care service hours to be included in Direct Care Service Hours Per Patient Day are accounted for with an original signature.";

export const COLUMN_HEADERS = [
	"NURSING SERVICES ASSIGNMENT",
	"EMPLOYEE NAME",
	"DISCIPLINE",
	"ACTUAL SHIFT START/ END",
	"ACTUAL MEAL BREAK START/END",
	"EMPLOYEE SIGNATURE",
];

/** Column widths of the official form, in points. Also drives the Excel column proportions. */
export const COLUMN_WIDTHS = [150, 150, 70, 120, 120, 110];

/** One table row, positionally aligned with `COLUMN_HEADERS`. */
export type Row530 = [string, string, string, string, string, string];

export type Page530 = { shift: Shift530; dateLabel: string; rows: Row530[] };

// Canonical shift starts (AM 07:00, PM 15:00, NOC 23:00). A nurse is bucketed to the shift whose
// start time is closest to their own start, not by fixed windows.
export const SHIFTS: Shift530[] = [
	{ id: "1", startTimeLabel: "7:00 AM", startHour: 7 },
	{ id: "2", startTimeLabel: "3:00 PM", startHour: 15 },
	{ id: "3", startTimeLabel: "11:00 PM", startHour: 23 },
];

// Hours wrap at midnight, so 02:00 is 3h from the 23:00 night start, not 21h.
function circularHourDistance(a: number, b: number): number {
	const diff = Math.abs(a - b);
	return Math.min(diff, 24 - diff);
}

function closestShiftId(hour: number): string {
	return SHIFTS.reduce((closest, shift) => (circularHourDistance(hour, shift.startHour) < circularHourDistance(hour, closest.startHour) ? shift : closest)).id;
}

/** Which of the three canonical shifts a scheduled shift or a punch belongs to. */
function shiftIdFor(nurse: NurseInShift): string {
	return closestShiftId(nurse.startTime.hour + nurse.startTime.minute / 60);
}

function nursesForShift(nurses: NurseInShift[], shiftId: string): NurseInShift[] {
	return nurses.filter((nurse) => shiftIdFor(nurse) === shiftId);
}

function scheduledOverlapMs(scheduled: NurseInShift, actual: NurseInShift): number {
	const start = Math.max(scheduled.startTime.toMillis(), actual.startTime.toMillis());
	const end = Math.min(scheduled.endTime.toMillis(), actual.endTime.toMillis());
	return Math.max(0, end - start);
}

// When several actual punches match one scheduled shift, pick the greatest-overlap one (not array order).
function findActual(scheduled: NurseInShift, actualNurses: NurseInShift[]): NurseInShift | undefined {
	const candidates = actualNurses.filter((actual) => actual.nurseId === scheduled.nurseId && actualMatchesScheduled(scheduled, actual));
	if (candidates.length <= 1) return candidates[0];
	return candidates.reduce((best, current) => (scheduledOverlapMs(scheduled, current) > scheduledOverlapMs(scheduled, best) ? current : best));
}

// Prefer punched times; fall back to scheduled per-side so a partial punch still reflects the real side.
function workedEnd(scheduled: NurseInShift, actual: NurseInShift | undefined): DateTime {
	return actual && !actual.missingPunchOut ? actual.endTime : scheduled.endTime;
}

// A nurse crosses into the next patient day when their worked end runs past the midnight that ends this one.
function crossesMidnight(nurse: NurseInShift, actualNurses: NurseInShift[], dayBoundary: DateTime): boolean {
	return workedEnd(nurse, findActual(nurse, actualNurses)).toMillis() > dayBoundary.toMillis();
}

// Pharos requires staff grouped by discipline in this fixed order; any other discipline sorts after.
const DISCIPLINE_RANK: Record<string, number> = { CNA: 0, LVN: 1, RN: 2 };
const UNRANKED_DISCIPLINE = 99;

function disciplineRank(role: NurseFullRole): number {
	return DISCIPLINE_RANK[NurseFullRoleShort[role]] ?? UNRANKED_DISCIPLINE;
}

type BuildRowsContext = { formatName: (nurse: NurseInShift) => string; settings: Form530Settings; timezone: string };

// The meal break and signature are always left blank so employees enter and sign for their own actual
// break and attendance. Employee name and discipline (role) are always pre-filled; the assignment and
// the actual shift times are pre-filled only when the facility asked for them (APP-647) — off, they stay
// blank so a scheduled nurse who hasn't punched is not misrepresented as having worked. Names come in as
// "Last, First" so the column sorts by last name.
function buildRows(nurses: NurseInShift[], { formatName, settings, timezone }: BuildRowsContext): Row530[] {
	// `nurseId`/`id` break the final tie so two same-role staff who format to the same display name (a
	// facility with two "J. Smith, RN"s) always land in the same order. Without them the comparator
	// returns 0 and the roster follows whatever order the API happened to return, so the same patient
	// day could produce two different forms.
	const sorted = [...nurses].sort(
		(a, b) =>
			disciplineRank(a.role) - disciplineRank(b.role) || formatName(a).localeCompare(formatName(b)) || a.nurseId - b.nurseId || a.id.localeCompare(b.id),
	);
	const rows: Row530[] = sorted.map((nurse) => [
		settings.showLocations ? (nurse.location ?? "") : "",
		formatName(nurse),
		NurseFullRoleShort[nurse.role],
		settings.showShiftTimes ? formatShiftTimeRange(nurse.startTime, nurse.endTime, timezone) : "",
		"",
		SIGNATURE_MARK,
	]);
	for (let i = rows.length; i < MIN_TABLE_ROWS; i++) {
		rows.push(["", "", "", "", "", SIGNATURE_MARK]);
	}
	return rows;
}

export type Build530PagesArgs = {
	scheduledNurses: NurseInShift[];
	actualNurses: NurseInShift[];
	date: DateTime;
	settings: Form530Settings;
	/** Department timezone — the zone pre-filled shift times are displayed in. */
	timezone: string;
	formatName: (nurse: NurseInShift) => string;
};

/**
 * The ordered pages of one patient day's 530, one per selected shift (plus a next-day continuation for
 * any shift whose staff work past midnight).
 *
 * Everyone the patient day has to account for: the whole schedule, plus anyone who punched without
 * being scheduled. Pairing is per SHIFT, not per nurse: a punch is redundant only when the schedule
 * already puts that nurse on the same shift's page. That drops the duplicate a low-overlap punch would
 * otherwise cause (common on nights, where punches get cut at the day boundary) while still surfacing
 * someone who punched into a shift they were not assigned to — they belong on the page they actually
 * worked, and an unsigned spare line is far cheaper than omitting a person from a state sign-in sheet.
 */
export function build530Pages({ scheduledNurses, actualNurses, date, settings, timezone, formatName }: Build530PagesArgs): Page530[] {
	const dateLabel = date.toFormat("MM/dd/yy");
	const nextDateLabel = date.plus({ days: 1 }).toFormat("MM/dd/yy");
	const dayBoundary = date.startOf("day").plus({ days: 1 });
	const context: BuildRowsContext = { formatName, settings, timezone };

	const unscheduledActuals = actualNurses.filter(
		(actual) => !scheduledNurses.some((scheduled) => scheduled.nurseId === actual.nurseId && shiftIdFor(scheduled) === shiftIdFor(actual)),
	);
	const sheetNurses = [...scheduledNurses, ...unscheduledActuals];
	const selectedShifts = settings.shiftFilter === "all" ? SHIFTS : SHIFTS.filter((shift) => shift.id === settings.shiftFilter);

	const pages: Page530[] = [];
	for (const shift of selectedShifts) {
		const shiftNurses = nursesForShift(sheetNurses, shift.id);
		if (shiftNurses.length === 0) continue;
		// The shift's own patient-day page lists everyone; nurses whose shift runs past midnight also
		// appear on the next patient day's page so each day's sheet shows who was present.
		pages.push({ shift, dateLabel, rows: buildRows(shiftNurses, context) });
		const crossers = shiftNurses.filter((nurse) => crossesMidnight(nurse, actualNurses, dayBoundary));
		if (crossers.length > 0) pages.push({ shift, dateLabel: nextDateLabel, rows: buildRows(crossers, context) });
	}

	// Always emit at least one page so an empty selection still produces a usable blank form.
	if (pages.length > 0) return pages;
	return [{ shift: selectedShifts[0] ?? SHIFTS[0], dateLabel, rows: buildRows([], context) }];
}
